import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import ReactDatePicker from "react-datepicker";
import { Button } from "../../common/Button/Button";
import api from "../../utils/api";
import DeliveredResourcePieChart from "./DeliveredResourcePieChart";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import downloadIcon from "../../assets/downloadsvg2.svg";
import { useHistory } from "react-router-dom";

// Memoized KPI Card component to avoid recreating DOM subtrees on parent renders
const DashboardKpiCard = React.memo(({ title, value, subtitle, accent, loading = false }) => {
    return (
        <div className={`dashboard-kpi-card ${accent}`}>
            <div className="dashboard-kpi-label">{title}</div>
            <div className="dashboard-kpi-value">
                {loading ? "..." : (value ?? 0)}
            </div>
            <div className="dashboard-kpi-subtitle">{subtitle}</div>
        </div>
    );
});
DashboardKpiCard.displayName = "DashboardKpiCard";

const adjustDateForStartOfDay = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    d.setTime(d.getTime() + (5.5 * 60 * 60 * 1000));
    return d;
};

const adjustDateForEndOfDay = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    d.setTime(d.getTime() + (29.5 * 60 * 60 * 1000 - 1000));
    return d;
};

const getCurrentDateFormatted = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = months[today.getMonth()];
    const year = today.getFullYear();
    return `${day}_${month}_${year}`;
};

const flattenInterventionsData = (data) => {
    if (!Array.isArray(data)) return [];
    const flattened = [];
    data.forEach((item, userIdx) => {
        const interventions = item?.interventions_availed;
        const isEvenUser = userIdx % 2 === 0;
        if (Array.isArray(interventions) && interventions.length > 0) {
            const count = interventions.length;
            interventions.forEach((intervention, index) => {
                let resourceName = "—";
                let statusVal = "—";
                if (typeof intervention === "object" && intervention !== null) {
                    resourceName = intervention.sResourceID || intervention.sResourceName || intervention.name || "—";
                    statusVal = intervention.Status || intervention.status || "—";
                } else if (typeof intervention === "string" && intervention) {
                    resourceName = intervention;
                    statusVal = item?.Status || item?.status || "—";
                }

                flattened.push({
                    ...item,
                    interventions_availed: resourceName,
                    Status: statusVal,
                    status: statusVal,
                    rowSpanCount: index === 0 ? count : 1,
                    isSubRow: index !== 0,
                    userIdx: userIdx,
                    isEvenUser: isEvenUser,
                    userOrder: userIdx * 100 + index,
                });
            });
        } else {
            flattened.push({
                ...item,
                interventions_availed: (typeof interventions === "string" && interventions) ? interventions : "—",
                Status: item?.Status || item?.status || "—",
                status: item?.Status || item?.status || "—",
                rowSpanCount: 1,
                isSubRow: false,
                userIdx: userIdx,
                isEvenUser: isEvenUser,
                userOrder: userIdx * 100,
            });
        }
    });
    return flattened;
};

const userRowSpan = (params) => {
    if (params.data && params.data.rowSpanCount > 1) {
        return params.data.rowSpanCount;
    }
    return 1;
};

const getUserBgColor = (params) => {
    return params.data?.isEvenUser === false ? "#ECECEC" : "#DEDEDE";
};

const spanCellStyle = (params) => {
    return {
        backgroundColor: getUserBgColor(params),
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid black",
    };
};

const interventionCellStyle = (params) => {
    return {
        backgroundColor: getUserBgColor(params),
        display: "flex",
        alignItems: "center",
    };
};

const AnekDashboardContainer = () => {
    const partnerGridRef = useRef(null);
    const interventionsGridRef = useRef(null);
    const hasShownMobileToastRef = useRef(false);

    // Filter states
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [quickFilterText, setQuickFilterText] = useState("");
    const [partnerSearchText, setPartnerSearchText] = useState("");
    const [mobileSearchText, setMobileSearchText] = useState("");

    // Loading states
    const [isDashboardLoading, setIsDashboardLoading] = useState(false);
    const [kpiLoading, setKpiLoading] = useState(false);
    const [pieChartLoading, setPieChartLoading] = useState(false);
    const [partnerWiseLoading, setPartnerWiseLoading] = useState(false);
    const [eligibleUsersLoading, setEligibleUsersLoading] = useState(false);

    // KPI data
    const [mobilizationCountData, setMobilizationCountData] = useState(0);
    const [countData, setCountData] = useState(0);
    const [availedCount, setAvailedCount] = useState(0);
    const [activePartnerCount, setActivePartnerCount] = useState(0);

    // Pie chart data
    const [summaryData, setSummaryData] = useState([]);
    const [totalResourcesDelivered, setTotalResourcesDelivered] = useState(0);

    // Partner-wise table data
    const [partnerWiseReport, setPartnerWiseReport] = useState([]);
    const [partnerWiseTotal, setPartnerWiseTotal] = useState({ registered: 0, availed: 0, conversion: 0 });

    // Interventions availed table data
    const [eligibleUsersData, setEligibleUsersData] = useState([]);

    const history = useHistory();

    const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

    // Column definitions for Partner-wise grid
    const partnerColumnDefs = useMemo(() => [
        {
            field: "partner",
            headerName: "Partner Name",
            flex: 2,
            minWidth: 200,
            sortable: false,
            // filter: true,
            cellRenderer: (params) => params.value || "Not Provided",
        },
        {
            field: "registered",
            headerName: "First Time Users",
            flex: 1,
            minWidth: 120,
            sortable: false,
            // filter: "agNumberColumnFilter",
            cellStyle: { textAlign: "right" },
            headerClass: "conv-text-right",
            valueFormatter: (params) => params.value ?? 0,
        },
        {
            field: "availed",
            headerName: "Engaged Users",
            flex: 1,
            minWidth: 120,
            sortable: false,
            // filter: "agNumberColumnFilter",
            cellStyle: { textAlign: "right" },
            headerClass: "conv-text-right",
            valueFormatter: (params) => params.value ?? 0,
        },
        // {
        //     field: "conversion",
        //     headerName: "Conversion",
        //     flex: 1,
        //     minWidth: 120,
        //     sortable: false,
        //     // filter: "agNumberColumnFilter",
        //     cellStyle: {
        //         textAlign: "right",
        //         fontWeight: "600",
        //     },
        //     headerClass: "conv-text-right",
        //     valueFormatter: (params) => `${Number(params.value ?? 0).toFixed(2)}%`,
        // },
    ], []);

    // Column definitions for Interventions Availed grid
    const eligibleUsersColumnDefs = useMemo(() => [
        {
            field: "sMobile",
            headerName: "Mobile Number",
            flex: 1,
            minWidth: 160,
            sortable: false,
            filter: true,
            rowSpan: userRowSpan,
            cellStyle: spanCellStyle,
            cellRenderer: (params) => {
                if (params.data?.isSubRow) return "";
                return params.value || "—";
            },
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 160,
            sortable: false,
            filter: true,
            rowSpan: userRowSpan,
            cellStyle: spanCellStyle,
            valueGetter: (params) => {
                if (params.data?.isSubRow) return "";
                const first = params.data?.FirstName || "";
                const last = params.data?.LastName || "";
                const fullName = `${first} ${last}`.trim();
                return fullName || "—";
            },
        },
        {
            field: "partner",
            headerName: "Partner",
            flex: 1,
            minWidth: 160,
            sortable: false,
            // filter: true,
            rowSpan: userRowSpan,
            cellStyle: spanCellStyle,
            cellRenderer: (params) => {
                if (params.data?.isSubRow) return "";
                return params.value || "Not Provided";
            },
        },
        {
            field: "interventions_availed",
            headerName: "Interventions Availed",
            flex: 2,
            minWidth: 200,
            sortable: false,
            // filter: true,
            cellStyle: interventionCellStyle,
            cellRenderer: (params) => {
                const value = params.value;
                if (!value || (Array.isArray(value) && !value.length) || value === "—") {
                    return "—";
                }
                if (Array.isArray(value)) {
                    return value.join(", ");
                }
                return value;
            },
        },
        {
            field: "Status",
            headerName: "Status",
            flex: 1,
            minWidth: 140,
            sortable: false,
            // filter: true,
            cellStyle: interventionCellStyle,
            cellRenderer: (params) => {
                const value = params.value || params.data?.status;
                if (!value || value === "—") {
                    return "—";
                }
                return value;
            },
        },
    ], []);

    // Footer row pinned to bottom of Partner-wise table
    const footerRow = useMemo(() => [{
        partner: "Total",
        registered: partnerWiseTotal?.registered ?? 0,
        availed: partnerWiseTotal?.availed ?? 0,
        conversion: partnerWiseTotal?.conversion ?? 0,
    }], [partnerWiseTotal]);

    const gridOptions = useMemo(() => ({
        suppressScrollOnNewData: true,
    }), []);

    const partnerRowClassRules = useMemo(() => ({
        "partner-total-row": (params) => params.data?.partner === "Total",
        "total-row": (params) => params.data?.partner === "Total",
    }), []);

    const interventionsRowClassRules = useMemo(() => ({
        "interventions-user-even": (params) => params.data?.isEvenUser === true,
        "interventions-user-odd": (params) => params.data?.isEvenUser === false,
    }), []);

    // Show loading overlay for Partner-wise table
    useEffect(() => {
        if (partnerGridRef.current && partnerGridRef.current.api) {
            if (partnerWiseLoading) {
                partnerGridRef.current.api.showLoadingOverlay();
            } else if (!partnerWiseReport || partnerWiseReport.length === 0) {
                partnerGridRef.current.api.showNoRowsOverlay();
            } else {
                partnerGridRef.current.api.hideOverlay();
            }
        }
    }, [partnerWiseLoading, partnerWiseReport]);

    // Show loading overlay for Interventions Availed table
    useEffect(() => {
        if (interventionsGridRef.current && interventionsGridRef.current.api) {
            if (eligibleUsersLoading) {
                interventionsGridRef.current.api.showLoadingOverlay();
            } else if (!eligibleUsersData || eligibleUsersData.length === 0) {
                interventionsGridRef.current.api.showNoRowsOverlay();
            } else {
                interventionsGridRef.current.api.hideOverlay();
            }
        }
    }, [eligibleUsersLoading, eligibleUsersData]);

    // Mobile resolution check (toast once)
    useEffect(() => {
        const mobileWidthBreakpoint = 768;

        const checkMobileResolution = () => {
            if (window.innerWidth < mobileWidthBreakpoint && !hasShownMobileToastRef.current) {
                hasShownMobileToastRef.current = true;
                toast("This dashboard is best viewable in a desktop browser.", {
                    autoClose: 5000,
                    type: toast.TYPE.ERROR,
                });
            }
        };

        checkMobileResolution();
        window.addEventListener("resize", checkMobileResolution);
        return () => window.removeEventListener("resize", checkMobileResolution);
    }, []);

    // Fetch partner-wise report data
    const fetchPartnerWiseData = useCallback((currentFromDate, currentToDate, partnerName) => {
        setPartnerWiseLoading(true);

        const filterParameters = {
            sPrograme_type: "Anek_Programe",
        };

        if (currentFromDate) {
            filterParameters.fromDate = adjustDateForStartOfDay(currentFromDate);
        }
        if (currentToDate) {
            filterParameters.toDate = adjustDateForEndOfDay(currentToDate);
        }
        if (partnerName && partnerName.trim()) {
            filterParameters.sPartnerName = partnerName.trim();
        }

        return api.partnerWiseRecordsData(filterParameters)
            .then(({ data }) => {
                setPartnerWiseReport(data?.data || []);
                setPartnerWiseTotal(data?.total || { registered: 0, availed: 0, conversion: 0 });
            })
            .catch((err) => {
                console.error("Error fetching partner-wise report:", err);
                setPartnerWiseReport([]);
                setPartnerWiseTotal({ registered: 0, availed: 0, conversion: 0 });
            })
            .finally(() => {
                setPartnerWiseLoading(false);
            });
    }, []);

    // Fetch interventions availed report data
    const fetchInterventionsData = useCallback((currentFromDate, currentToDate, mobileNumber) => {
        setEligibleUsersLoading(true);

        const filterParameters = {
            sPrograme_type: "Anek_Programe",
        };

        if (currentFromDate) {
            filterParameters.fromDate = adjustDateForStartOfDay(currentFromDate);
        }
        if (currentToDate) {
            filterParameters.toDate = adjustDateForEndOfDay(currentToDate);
        }
        if (mobileNumber && mobileNumber.trim()) {
            filterParameters.sMobile = mobileNumber.trim();
        }

        return api.partnerInverntionsAvailed(filterParameters)
            .then(({ data }) => {
                setEligibleUsersData(flattenInterventionsData(data?.data || []));
            })
            .catch((err) => {
                console.error("Error fetching interventions availed data:", err);
                setEligibleUsersData([]);
            })
            .finally(() => {
                setEligibleUsersLoading(false);
            });
    }, []);

    // Optimized Dashboard Data Fetching (only 5 essential endpoints called in parallel)
    const fetchDashboardData = useCallback(async (currentFromDate, currentToDate, partnerName, mobileNumber) => {
        setIsDashboardLoading(true);
        setKpiLoading(true);
        setPieChartLoading(true);

        const filterParameters = {
            sPrograme_type: "Anek_Programe",
        };

        if (currentFromDate) {
            filterParameters.fromDate = adjustDateForStartOfDay(currentFromDate);
        }
        if (currentToDate) {
            filterParameters.toDate = adjustDateForEndOfDay(currentToDate);
        }

        // 1. Mobilization count (Registered users)
        const mobilizationPromise = api.mobilizationCountAnekApi({ api_key: "r54B?rHXUE7v%$9t" })
            .then(({ data }) => {
                setMobilizationCountData(data?.data?.total_count ?? 0);
            })
            .catch((err) => {
                console.error("Error fetching mobilization count:", err);
                setMobilizationCountData(0);
            });

        // 2. Count metrics (Eligible, Attended, Active Partners)
        const countsPromise = api.jpmcCountData(filterParameters)
            .then(({ data }) => {
                setCountData(data?.count ?? 0);
                setAvailedCount(data?.users_attended ?? 0);
                setActivePartnerCount(data?.active_partners ?? 0);
            })
            .catch((err) => {
                console.error("Error fetching count data:", err);
                setCountData(0);
                setAvailedCount(0);
                setActivePartnerCount(0);
            });

        // 3. Delivered resources for Pie Chart
        const deliveredPromise = api.allJpmcDeliveredCounts(filterParameters)
            .then(({ data }) => {
                const {
                    Compliances_count = 0,
                    ExpertConsultations_count = 0,
                    MarketOpportunities_count = 0,
                    KnowledgeSessions_count = 0,
                    LoanReadiness_count = 0,
                } = data || {};

                const total = Compliances_count + ExpertConsultations_count + MarketOpportunities_count + KnowledgeSessions_count + LoanReadiness_count;

                setSummaryData([
                    ["Expert Consultation", ExpertConsultations_count],
                    ["Market Connect", MarketOpportunities_count],
                    ["Group Knowledge Session", KnowledgeSessions_count],
                    ["Compliance", Compliances_count],
                    ["Business Loan Readiness", LoanReadiness_count],
                ]);
                setTotalResourcesDelivered(total);
            })
            .catch((err) => {
                console.error("Error fetching delivered counts:", err);
                setSummaryData([]);
                setTotalResourcesDelivered(0);
            })
            .finally(() => {
                setPieChartLoading(false);
            });

        // 4. Partner-wise report
        const partnerWisePromise = fetchPartnerWiseData(currentFromDate, currentToDate, partnerName);

        // 5. Interventions availed by user
        // const interventionsPromise = fetchInterventionsData(currentFromDate, currentToDate, mobileNumber);

        // Resolve KPI metrics first
        await Promise.allSettled([mobilizationPromise, countsPromise]);
        setKpiLoading(false);

        // Await all other promises
        await Promise.allSettled([deliveredPromise, partnerWisePromise]);
        setIsDashboardLoading(false);
    }, [fetchPartnerWiseData]);

    // Initial load on mount
    useEffect(() => {
        fetchDashboardData(null, null, "", "");
    }, [fetchDashboardData]);

    const handleSearch = () => {
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            toast.error("From Date cannot be after To Date.");
            return;
        }
        fetchDashboardData(fromDate, toDate, partnerSearchText, mobileSearchText);
    };

    const handleClear = () => {
        setFromDate(null);
        setToDate(null);
        setQuickFilterText("");
        setPartnerSearchText("");
        setMobileSearchText("");
        if (interventionsGridRef.current && interventionsGridRef.current.api) {
            interventionsGridRef.current.api.setQuickFilter("");
        }
        fetchDashboardData(null, null, "", "");
    };

    const handlePartnerSearch = () => {
        fetchPartnerWiseData(fromDate, toDate, partnerSearchText);
    };

    const handlePartnerClear = () => {
        setPartnerSearchText("");
        fetchPartnerWiseData(fromDate, toDate, "");
    };

    const handlePartnerSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handlePartnerSearch();
        }
    };

    const handleMobileSearch = () => {
        fetchInterventionsData(fromDate, toDate, mobileSearchText);
    };

    const handleMobileClear = () => {
        setMobileSearchText("");
        fetchInterventionsData(fromDate, toDate, "");
    };

    const handleMobileSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleMobileSearch();
        }
    };

    const handleExportPartnerCsv = useCallback(() => {
        if (partnerGridRef.current && partnerGridRef.current.api) {
            partnerGridRef.current.api.exportDataAsCsv({
                fileName: `Anek_Partner_Wise_Report_${getCurrentDateFormatted()}.csv`,
            });
        }
    }, []);

    const handleExportInterventionsCsv = useCallback(() => {
        if (interventionsGridRef.current && interventionsGridRef.current.api) {
            interventionsGridRef.current.api.exportDataAsCsv({
                fileName: `Anek_Interventions_Availed_${getCurrentDateFormatted()}.csv`,
            });
        }
    }, []);

    const onQuickFilterChange = useCallback((e) => {
        const text = e.target.value;
        setQuickFilterText(text);
        if (interventionsGridRef.current && interventionsGridRef.current.api) {
            interventionsGridRef.current.api.setQuickFilter(text);
        }
    }, []);

    return (
        <div>
            {/* Filter Bar */}
            <div className="row mt-2 ml-1 mr-1 justify-content-between align-items-center">
                <div className="col-12 col-lg-8 col-xl-9">
                    <div className="row align-items-center">

                        {/* From Date */}
                        <div className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3 spled-container conv-zindex mb-2 mb-md-0">
                            <div className="width-100">
                                <ReactDatePicker
                                    dateFormat="dd/MM/yyyy"
                                    selected={fromDate}
                                    onChange={(date) => setFromDate(date)}
                                    placeholderText="From Date"
                                    minDate={new Date("01-01-2024")}
                                    maxDate={new Date()}
                                    isClearable
                                />
                            </div>
                        </div>

                        {/* To Date */}
                        <div className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3 spled-container conv-zindex mb-2 mb-md-0">
                            <div className="width-100">
                                <ReactDatePicker
                                    dateFormat="dd/MM/yyyy"
                                    selected={toDate}
                                    onChange={(date) => setToDate(date)}
                                    placeholderText="To Date"
                                    minDate={fromDate || new Date("01-01-2024")}
                                    maxDate={new Date()}
                                    isClearable
                                />
                            </div>
                        </div>

                        {/* Search and Clear Buttons */}
                        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-5 spled-container">
                            <div className="row align-items-center conv-pl10" style={{ gap: "10px" }}>
                                <div
                                    className="col-auto"
                                    style={{ padding: "0px", marginTop: "25px" }}
                                >
                                    <Button
                                        className="esp-inline-button"
                                        style={{ height: "41px", minWidth: "90px" }}
                                        onClick={handleSearch}
                                        disabled={isDashboardLoading}
                                    >
                                        {isDashboardLoading ? "Searching..." : "Search"}
                                    </Button>
                                </div>

                                <div
                                    className="col-auto"
                                    style={{ padding: "0px", marginTop: "25px" }}
                                >
                                    <Button
                                        className="esp-inline-button"
                                        style={{ height: "41px", minWidth: "90px" }}
                                        onClick={handleClear}
                                        disabled={isDashboardLoading}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Interventions Availed Reports Link */}
                <div className="col-12 col-lg-4 col-xl-3 text-left text-lg-right mt-2 mt-lg-0 pr-3">
                    <div className="d-flex align-items-center justify-content-start justify-content-lg-end" style={{ marginTop: "25px", marginBottom: "24px" }}>
                        <a
                            onClick={() => { history.push("/home/anekdashboard-interventions-availed-reports/"); }}
                            className="con-cursor-pointer"
                            style={{
                                color: "#78417C",
                                textDecoration: "none",
                            }}
                        >
                            <span className="text-hover" style={{ fontSize: "17px", cursor: "pointer" }}>
                                Interventions Availed Reports
                            </span>
                        </a>
                    </div>
                </div>
            </div>

            {/* KPI Cards Row */}
            <div className="dashboard-kpi-row">
                {/* <DashboardKpiCard
                    title="Users registered"
                    value={mobilizationCountData}
                    subtitle="Completed registration on any landing page"
                    accent="#e3e7eb"
                    loading={kpiLoading}
                /> */}

                <DashboardKpiCard
                    title="First Time Users"
                    value={countData}
                    subtitle="First Time users"
                    accent="#e3e7eb"
                    loading={kpiLoading}
                />

                <DashboardKpiCard
                    title="Engaged Users"
                    value={availedCount}
                    subtitle="Users who availed services"
                    accent="#e3e7eb"
                    loading={kpiLoading}
                />

                <DashboardKpiCard
                    title="Active Partners"
                    value={activePartnerCount}
                    subtitle="Tagged automatically via landing page source"
                    accent="#e3e7eb"
                    loading={kpiLoading}
                />
            </div>

            <div style={{ borderBottom: "2px solid lightgray" }}></div>

            {/* Charts & Partner Report Section */}
            <div className="row mt-3 mb-2 partner-data-section">
                {/* LEFT - Delivered Resource Pie Chart */}
                <div className="col-lg-4 col-md-12 col-sm-12 mt-2">
                    <DeliveredResourcePieChart
                        data={summaryData}
                        totalResourcesDelivered={totalResourcesDelivered}
                        entrepreneursCount={totalResourcesDelivered}
                        isGetAllDeliveredCountLoading={pieChartLoading}
                        isComplianceLoading={false}
                        isMarketConnectLoading={false}
                        isExpertConsultationLoading={false}
                        isGroupKnowledgeSessionLoading={false}
                        isBlrpLoading={false}
                    />
                </div>

                {/* RIGHT - Partner-wise Report Grid */}
                <div className="col-lg-8 col-md-12 col-sm-12 mt-2">
                    <div
                        className="partner-data-header"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                            marginBottom: "8px",
                        }}
                    >
                        <div className="partner-data-title">
                            <h3 className="spled-colored-label">Partner-wise report</h3>
                        </div>

                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <input
                                type="text"
                                placeholder="Search by partner name..."
                                value={partnerSearchText}
                                onChange={(e) => setPartnerSearchText(e.target.value)}
                                onKeyDown={handlePartnerSearchKeyDown}
                                className="form-control"
                                style={{
                                    width: "200px",
                                    height: "34px",
                                    fontSize: "13px",
                                    padding: "4px 8px",
                                }}
                            />
                            <Button
                                className="esp-inline-button"
                                style={{ height: "34px", minWidth: "75px", margin: 0, padding: "0 10px", fontSize: "13px" }}
                                onClick={handlePartnerSearch}
                                disabled={partnerWiseLoading}
                            >
                                {partnerWiseLoading ? "Searching..." : "Search"}
                            </Button>
                            {partnerSearchText && (
                                <Button
                                    className="esp-inline-button"
                                    style={{ height: "34px", minWidth: "60px", margin: 0, padding: "0 10px", fontSize: "13px" }}
                                    onClick={handlePartnerClear}
                                    disabled={partnerWiseLoading}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </div>

                    <div style={{ height: "50vh" }}>
                        <div style={{ height: "100%", boxSizing: "border-box" }}>
                            <div style={gridStyle} className="ag-theme-material">
                                <AgGridReact
                                    ref={partnerGridRef}
                                    rowData={partnerWiseReport}
                                    columnDefs={partnerColumnDefs}
                                    pinnedBottomRowData={footerRow}
                                    gridOptions={gridOptions}
                                    // rowClassRules={partnerRowClassRules}
                                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Loading...</span>'}
                                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No partner records found</span>'}
                                     rowClassRules={{
                                        'total-row': 'data.partner === "Total"'
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* BOTTOM - Interventions Availed by Mobile Number */}
            {/* <div className="row mt-3 mb-2">
                <div className="col-12">
                    <div
                        className="partner-data-header"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "10px",
                            marginBottom: "8px",
                        }}
                    >
                        <div className="partner-data-title">
                            <h3 className="spled-colored-label">
                                Interventions availed — by Mobile Number
                            </h3>
                        </div>

                        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                            <input
                                type="text"
                                placeholder="Search by mobile number..."
                                value={mobileSearchText}
                                onChange={(e) => setMobileSearchText(e.target.value)}
                                onKeyDown={handleMobileSearchKeyDown}
                                className="form-control"
                                style={{
                                    width: "200px",
                                    height: "34px",
                                    fontSize: "13px",
                                    padding: "4px 8px",
                                }}
                            />
                            <Button
                                className="esp-inline-button"
                                style={{ height: "34px", minWidth: "75px", margin: 0, padding: "0 10px", fontSize: "13px" }}
                                onClick={handleMobileSearch}
                                disabled={eligibleUsersLoading}
                            >
                                {eligibleUsersLoading ? "Searching..." : "Search"}
                            </Button>
                            {mobileSearchText && (
                                <Button
                                    className="esp-inline-button"
                                    style={{ height: "34px", minWidth: "60px", margin: 0, padding: "0 10px", fontSize: "13px" }}
                                    onClick={handleMobileClear}
                                    disabled={eligibleUsersLoading}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </div>

                    <div style={{ height: "50vh", width: "100%" }}>
                        <div style={{ height: "100%", width: "100%" }} className="ag-theme-material">
                            <AgGridReact
                                ref={interventionsGridRef}
                                rowData={eligibleUsersData}
                                columnDefs={eligibleUsersColumnDefs}
                                pagination={true}
                                paginationPageSize={10}
                                suppressScrollOnNewData={true}
                                suppressRowTransform={true}
                                rowClassRules={interventionsRowClassRules}
                                overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Loading...</span>'}
                                overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No records found</span>'}
                            />
                        </div>
                    </div>
                </div>
            </div> */}
        </div>
    );
};

export default AnekDashboardContainer;
